/* eslint-disable complexity */
import React, { Component } from 'react';

import Button from '@atlaskit/button/standard-button';
import DropdownMenu, {
  DropdownItem,
  DropdownItemGroup,
} from '@atlaskit/dropdown-menu';
import EditorRemoveIcon from '@atlaskit/icon/glyph/editor/remove';
import MoreIcon from '@atlaskit/icon/glyph/more';

import { Button as SoldeveloButton } from '@qality/common-components/src/atoms/Button';
import { ExecutionStatusLozenge } from '@qality/common-components/src/atoms/ExecutionStatusLozenge';
import { Flex } from '@qality/common-components/src/atoms/Flex';
import { PremiumFeatureGuard } from '@qality/common-components/src/atoms/PremiumFeatureGuard';
import {
  Table,
  createColumnWidths,
  createColumnsWidths,
} from '@qality/common-components/src/atoms/Table';
import { Text } from '@qality/common-components/src/atoms/Text';
import { User } from '@qality/common-components/src/atoms/User';
import { Issue } from '@qality/common-components/src/molecules/Issue';
import { ReportedBugsListView } from '@qality/common-components/src/molecules/ReportedBugsList';
import DeleteTestExecutionDialog from '@qality/common-components/src/test-execution/DeleteTestExecutionDialog';
import {
  HistoryStep,
  ReportFetchResultPagination,
  TestCase,
} from '@qality/common-model';
import {
  DateFormatService,
  StorageOfUniqueServices,
  TestExecutionHelper,
} from '@qality/common-service';
import { StringUtils } from '@qality/common-utils';

import TestExecutionEmptyHistoryList from './TestExecutionEmptyHistoryList';
import TestExecutionNotFoundHistoryList from './TestExecutionNotFoundHistoryList';
import './_test-execution-history-list.scss';

type Columns =
  | 'createdOn'
  | 'executionAssignee'
  | 'status'
  | 'comment'
  | 'testCycle'
  | 'bugs'
  | 'actions';

const ColumnsWidths = createColumnsWidths<Columns>({
  createdOn: createColumnWidths('100px'),
  executionAssignee: createColumnWidths('auto', '150px'),
  status: createColumnWidths('120px'),
  comment: createColumnWidths(),
  testCycle: createColumnWidths('auto', '120px'),
  bugs: createColumnWidths('420px'),
  actions: createColumnWidths('100px'),
});

type Props = {
  historyList: Array<HistoryStep>;
  testCaseExecutionUrl: string;
  testCase: TestCase;
  pagination: ReportFetchResultPagination;
};

type State = {
  deleteExecutionDialogIsOpen?: boolean;
  testExecutionId?: number;
  openedDialogId?: number;
};

export default class TestExecutionHistoryList extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      openedDialogId: null,
    };
  }

  openDialog = (testExecutionId: number): void => {
    this.setState({ openedDialogId: testExecutionId });
  };

  closeDialog = (): void => this.setState({ openedDialogId: null });

  deleteExecution = (testExecutionId: number): void => {
    if (testExecutionId) {
      TestExecutionHelper.deleteTestExecution(testExecutionId);
    }
  };

  openTestExecutionDetailsPage = (testExecutionId: number): void => {
    window.open(this.props.testCaseExecutionUrl + testExecutionId, '_blank');
  };

  render(): React.ReactNode {
    const {
      historyList = [],
      pagination = {} as ReportFetchResultPagination,
      testCase,
    } = this.props;

    if (historyList.length <= 0 && pagination.currentPage === 0) {
      return <TestExecutionEmptyHistoryList testCase={testCase} />;
    }

    if (historyList.length <= 0) {
      return <TestExecutionNotFoundHistoryList />;
    }

    return (
      <Table data-testid='TestExecutionHistoryList-Table'>
        <Table.Head>
          <Table.Tr>
            <Table.Th widths={ColumnsWidths.createdOn}>Created on</Table.Th>
            <Table.Th widths={ColumnsWidths.executionAssignee}>
              Execution Assignee
            </Table.Th>
            <Table.Th widths={ColumnsWidths.status}>Status</Table.Th>
            <Table.Th widths={ColumnsWidths.comment}>Comment</Table.Th>
            <Table.Th widths={ColumnsWidths.testCycle}>Test Cycle</Table.Th>
            <PremiumFeatureGuard
              premium={<Table.Th widths={ColumnsWidths.bugs}>Bugs</Table.Th>}
            />
            <Table.Th widths={ColumnsWidths.actions}>Actions</Table.Th>
          </Table.Tr>
        </Table.Head>

        <Table.Body>
          {historyList.map((row, index) => {
            const doPrimaryAction = this.openTestExecutionDetailsPage.bind(
              this,
              row.id
            );

            return (
              <Table.Tr
                isClickable
                isHoverable
                data-testid='TestCyclesList-Tr'
                key={`${row.id}-${index}`}
                onClick={doPrimaryAction}
              >
                <Table.Td>
                  <Table.ContentHelper alignItems='center'>
                    {row.createdDate ? (
                      <Text>
                        {DateFormatService.formatDate(
                          row.createdDate,
                          'longDateFormat'
                        )}
                      </Text>
                    ) : (
                      <Text inactive>None</Text>
                    )}
                  </Table.ContentHelper>
                </Table.Td>
                <Table.Td>
                  <Table.ContentHelper alignItems='center'>
                    <User
                      userName={row.executionAssignee?.getDisplayName()}
                      userAvatar={row.executionAssignee?.getAvatar()}
                      size='small'
                    />
                  </Table.ContentHelper>
                </Table.Td>
                <Table.Td>
                  <Table.ContentHelper alignItems='center'>
                    <ExecutionStatusLozenge status={row.status} />
                  </Table.ContentHelper>
                </Table.Td>
                <Table.Td>
                  <Table.ContentHelper alignItems='center'>
                    {row.comment ? (
                      <div
                        className='text-wrapper test-execution-history-comment'
                        dangerouslySetInnerHTML={{
                          __html: StringUtils.toHTML(row.comment),
                        }}
                      />
                    ) : (
                      <Text inactive>None</Text>
                    )}
                  </Table.ContentHelper>
                </Table.Td>
                <Table.Td>
                  <Table.ContentHelper alignItems='center'>
                    {row.testCycle ? (
                      <Issue
                        title={row.testCycle.name}
                        issueLink={StorageOfUniqueServices.get(
                          'URLService'
                        ).getTestCycleUrl(row.testCycle.id)}
                      />
                    ) : (
                      <Text inactive>Ad hoc</Text>
                    )}
                  </Table.ContentHelper>
                </Table.Td>
                <PremiumFeatureGuard
                  premium={
                    <Table.Td>
                      <Table.ContentHelper alignItems='center'>
                        <ReportedBugsListView
                          errorsCount={row.linkedIssuesErrors}
                          issuesList={row.linkedIssues}
                          shouldAddStrikethrough={true}
                          isLoading={false}
                          readOnly
                        />
                      </Table.ContentHelper>
                    </Table.Td>
                  }
                />
                <Table.Td
                  onClick={(e) => e.stopPropagation()}
                  className='show-on-row-hover'
                >
                  <Flex spaceBetween>
                    <SoldeveloButton
                      mr
                      marginSize='small'
                      onClick={doPrimaryAction}
                    >
                      View
                    </SoldeveloButton>
                    <DropdownMenu
                      shouldFlip={false}
                      placement='left-start'
                      trigger={({ triggerRef, ...props }) => (
                        <Button
                          {...props}
                          iconBefore={
                            <MoreIcon
                              label={null}
                              size='small'
                              primaryColor='var(--ds-icon, var(--ds-fb-icon))'
                            />
                          }
                          ref={triggerRef}
                          appearance='default'
                        />
                      )}
                      testId='testCaseDropdown'
                    >
                      <DropdownItemGroup>
                        <DropdownItem
                          key='delete'
                          onClick={() => this.openDialog(row.id)}
                        >
                          <div className='qality-test-execution-history-actions-wrapper'>
                            <div className='qality-actions-icon'>
                              <EditorRemoveIcon
                                label={null}
                                primaryColor='var(--ds-icon, var(--ds-fb-icon))'
                              />
                            </div>
                            <div
                              className='qality-actions-label'
                              id='qality-test-case-execution-action-delete'
                            >
                              Delete
                            </div>
                          </div>
                        </DropdownItem>
                      </DropdownItemGroup>
                    </DropdownMenu>
                  </Flex>
                  <DeleteTestExecutionDialog
                    isOpen={this.state.openedDialogId === row.id}
                    onCancel={this.closeDialog}
                    onDelete={this.deleteExecution.bind(this, row.id)}
                  />
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Body>
      </Table>
    );
  }
}
